import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

/**
 * The behaviour of a menu button (CO-177), shared by `ActionMenu` and the
 * account menu so they cannot drift apart. The markup stays with each menu;
 * the hook owns what the keyboard and the pointer do:
 *
 * - a click or Enter opens it with focus on the first item; ArrowDown opens
 *   it on the first, ArrowUp on the last;
 * - while it is open the arrows, Home and End move through the items and wrap,
 *   wherever focus is, so a menu whose focus was lost (an item re-rendered,
 *   a screen reader moved the cursor) still answers;
 * - Escape closes it and returns focus to the button. The listener runs first
 *   and stops the event, so inside a dialog Escape closes only the menu and
 *   the dialog stays;
 * - Tab closes it and moves on as usual; a press outside closes it and leaves
 *   focus where the person put it.
 *
 * `items` returns the focusable items of the open panel, read from the DOM
 * each time because the list changes with the data and the viewport. A
 * disabled item (`aria-disabled`) stays among them: the arrows reach it so
 * the action is discoverable, and activating it does nothing (CO-184, DS3).
 */
export function useMenu({ items }: { items: (panel: HTMLElement) => HTMLElement[] }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const landOn = useRef<'first' | 'last'>('first');
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const focusables = useCallback(() => (panelRef.current ? itemsRef.current(panelRef.current) : []), []);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  // Opening moves focus into the panel, so a keyboard user does not have to
  // Tab past the menu to reach its first item.
  useEffect(() => {
    if (!open) return;
    const list = focusables();
    (landOn.current === 'last' ? list[list.length - 1] : list[0])?.focus();
    landOn.current = 'first';
  }, [open, focusables]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        // First in line: a dialog around the menu must not also see this.
        event.preventDefault();
        event.stopPropagation();
        close(true);
        return;
      }
      if (event.key === 'Tab') {
        setOpen(false);
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Home' && event.key !== 'End') return;
      const list = focusables();
      if (list.length === 0) return;
      event.preventDefault();
      const current = list.indexOf(document.activeElement as HTMLElement);
      let next: number;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = list.length - 1;
      else if (event.key === 'ArrowDown') next = current < 0 ? 0 : (current + 1) % list.length;
      else next = current < 0 ? list.length - 1 : (current - 1 + list.length) % list.length;
      list[next]?.focus();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, close, focusables]);

  /** Spread on the trigger button next to `ref={triggerRef}`. */
  const triggerProps = {
    'aria-haspopup': 'menu' as const,
    'aria-expanded': open,
    onClick: () => setOpen((value) => !value),
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (open || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
      event.preventDefault();
      landOn.current = event.key === 'ArrowUp' ? 'last' : 'first';
      setOpen(true);
    },
  };

  return { open, setOpen, close, rootRef, triggerRef, panelRef, triggerProps };
}

/**
 * The items of an ARIA menu: every `menuitem`, disabled ones included. A
 * disabled item is `aria-disabled`, not `disabled`, so it keeps focus and its
 * name stays readable; its click handler does nothing.
 */
export const menuItems = (panel: HTMLElement) => Array.from(panel.querySelectorAll<HTMLElement>('[role="menuitem"]'));
